<template>
  <div
    class="flex flex-row flex-1 min-w-[300px] rounded-lg shadow-[0px_-1px_16px_0px_rgba(0,0,0,0.12)] relative z-1"
    v-if="displayed"
  >
    <div
      class="w-2 rounded-l-lg min-w-[8px]"
      :class="vehicleNumberColor()"
    ></div>

    <div
      class="flex flex-col pr-4 pl-2 pt-2 items-center min-w-[120px] min-h-[110px] font-tahoma"
      :class="vehicleNumberBackground(isInactive())"
    >
      <div v-if="customer.customerName.value == 'oost'">
        <ShiftSelectInput
            v-model="shift"
            @update:model-value="(value) => updateShiftSelect(value as string)"
            :options="shiftOptions ? shiftOptions : []"
            :loading="isLoadingShifts"
        ></ShiftSelectInput>
      </div>
      <VehicleSelectInput
        :model-value="vehicleNumber"
        @update:model-value="(value) => updateVehicle(value as string | null)"
        :options="dataVehicle ? dataVehicle : []"
        :loading="isLoadingVehicle"
        :placeholder="
          !vehicleNumber
            ? ''
            : dataVehicle?.find((p) => p.Id === `${vehicleNumber}`)?.Label
        "
        :placeholder-selected="Boolean(vehicleNumber)"
        :text-color="vehicleNumberColor()"
        :class="{
          oost: customer.customerName.value == 'oost',
        }"
      >
      </VehicleSelectInput>
      <div class="text-base text-center w-[105px] font-calibri">
        <TimeSelectInput
          v-model="time"
          @update:model-value="(value) => updateTimeSelect(value as number)"
          :options="dataTimeOptions ? dataTimeOptions : []"
          :loading="isLoadingTime"
        ></TimeSelectInput>
      </div>
    </div>

    <div
      class="flex flex-col flex-1 py-2 gap-1 font-arial text-base min-w-[185px]"
      :class="personColor()"
      v-if="humanResourceResourceGroups"
    >
      <div
        v-for="humanResourceResourceGroup in sortHumanResources(
          humanResourceResourceGroups
        )"
        :key="`${schedule.Id}-hrg-${humanResourceResourceGroup.HumanResource.Id}`"
      >
        <PersonSelectInput
          :model-value="humanResourceResourceGroup.HumanResource.Id"
          @update:model-value="
            (value) =>
              updatePerson(humanResourceResourceGroup.HumanResource.Id, value)
          "
          :loading="isLoadingUpdatePerson"
          :placeholder="
            !humanResourceResourceGroup.HumanResource.Id
              ? ''
              : dataPerson?.find(
                  (p) =>
                    p.Id === `${humanResourceResourceGroup.HumanResource.Id}`
                )?.Label
          "
          :placeholder-selected="
            Boolean(humanResourceResourceGroup.HumanResource.Id)
          "
          :role="parseRole(humanResourceResourceGroup.ResourceGroup.Uname)"
          :options="dataPerson ? dataPerson : []"
        ></PersonSelectInput>
      </div>

      <div v-if="humanResourceResourceGroups.length < 3">
        <!-- show personselectinput 1 times-->
        <PersonSelectInput
          v-model="newPerson"
          :options="dataPerson ? dataPerson : []"
          placeholder=""
          :placeholder-selected="false"
        ></PersonSelectInput>
      </div>
    </div>
    <div v-if="isZwolleWestenholte()" :class="personColor()" class="w-0">
      <div
        class="font-bold text-2xl rotate-90 relative top-[10px] left-[-15px]"
      >
        west
      </div>
    </div>

    <RemarkInput
      @toggle="toggleRemarkInput"
      @update:remarkText="updateRemarkText"
      class="flex"
      :class="personColor()"
      :scheduleId="schedule.Id"
      :remarkText="remarkText"
      :hasRemark="hasRemark"
      :showTrashIcon="showTrashIcon"
      :isActive="activeScheduleId === schedule.Id"
      @close="handleClose"
    />

    <div
      class="flex w-4 rounded-r-lg hover:border hover:border-neutral-500"
      :class="scheduleStateColor()"
    >
      <StatusSelectInput
        :loading="isLoadingUpdateScheduleState"
        :model-value="scheduleState"
        @update:model-value="(value) => updateStatusSelect(value as string | null)"
      ></StatusSelectInput>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dateToTime } from "@planbord/shared/dates";
import { sharedState } from "@planbord/shared/dates";
import { Schedule, UpdatePersonRequest } from "@planbord/shared/models";
import {
  useUpdateVehicle,
  useUpdatePerson,
  useAddPerson,
  useUpdateScheduleState,
  useUpdateTime,
  useListPersonSelectOptions,
  useBroadcast,
  useListTimeSelectOptions,
  useListVehicleSelectOptions,
  useListShiftsSelectOptions,
  useUpdateShift
} from "@planbord/queries/planbord-api";
import { watch, ref, toRefs, onMounted, computed } from "vue";
import { HumanResourceResourceGroup } from "@planbord/shared/models";
import { useSignalR } from "@dreamonkey/vue-signalr";
import PersonSelectInput from "./PersonSelectInput.vue";
import StatusSelectInput from "./StatusSelectInput.vue";
import VehicleSelectInput from "./VehicleSelectInput.vue";
import TimeSelectInput from "./TimeSelectInput.vue";
import RemarkInput from "./RemarkInput.vue";
import { useCustomerName } from "@planbord/shared/stores";
import ShiftSelectInput from "./ShiftSelectInput.vue";

const customer = useCustomerName();

const props = defineProps<{
  schedule: Schedule;
  activeScheduleId: number | null;
  setActiveScheduleId: (id: number | null) => void;
}>();

// Destructure the props for easier access
const { activeScheduleId, setActiveScheduleId } = toRefs(props);

function handleClose() {
  setActiveScheduleId.value(null); // Ensure that setActiveScheduleId is a function
}

// Existing computed property
const isRemarkInputActive = computed(() => {
  return sharedState.activeRemarkInput.value === props.schedule.Id;
});

// Method to toggle RemarkInput
function toggleRemarkInput() {
  if (isRemarkInputActive.value) {
    props.setActiveScheduleId(null); // Close RemarkInput
  } else {
    props.setActiveScheduleId(props.schedule.Id); // Open RemarkInput
  }
}

let displayed = true;

const signalr = useSignalR();
const vehicleNumber = ref(props.schedule.Vehicle?.Id);
const humanResourceResourceGroups = ref(
  props.schedule.HumanResourceResourceGroups
);
const scheduleState = ref(props.schedule.ScheduleState?.Id);
const remarkText = ref(props.schedule.Remark?.RemarkText ?? "");
const hasRemark = ref(!!remarkText.value);
const showTrashIcon = ref(hasRemark.value);
const newPerson = ref();
const time = ref();
const dataTimeOptions = ref();
const shiftOptions = ref();
const shift = ref();

const { data: dataTime, isLoading: isLoadingTime } = useListTimeSelectOptions();
const { data: shifts, isLoading: isLoadingShifts } = useListShiftsSelectOptions();

const { data: dataVehicle, isLoading: isLoadingVehicle } =
  useListVehicleSelectOptions();

const { data: dataPerson } = useListPersonSelectOptions();

const { mutateAsync: updateVehicleAsync } = useUpdateVehicle();

const {
  mutateAsync: updateScheduleState,
  isLoading: isLoadingUpdateScheduleState,
} = useUpdateScheduleState();

const { mutateAsync: updateTime } = useUpdateTime();

const { mutateAsync: updateShift } = useUpdateShift();

const { mutateAsync: updatePersonAsync, isLoading: isLoadingUpdatePerson } =
  useUpdatePerson();

const { mutateAsync: addPersonAsync } = useAddPerson();

const { mutateAsync: broadcast } = useBroadcast();

function getTimeSelectOptionFromDataTime() {
  //combine start and end time from props schedule
  const startTime = dateToTime(props.schedule.StartTime);
  const endTime = dateToTime(props.schedule.EndTime);
  const combinedTime = `${startTime} - ${endTime}`;

  //check if time is available as a SelectOption in dataTime
  if (dataTime.value) {
    const timeSelectOption = dataTime.value.find(
      (x: { Label: string }) => x.Label === combinedTime
    );
    if (timeSelectOption) {
      // set the value from the dataTime
      dataTimeOptions.value = [...dataTime.value];
      // set the value of the time (selected item)
      time.value = timeSelectOption.Id;
    } else {
      //time is not available in dataTime, so add it to the dataTime
      const newTimeSelectOption = {
        Id: "0",
        Label: combinedTime,
      };

      // set the value from the dataTime with the extra option
      dataTimeOptions.value = [...dataTime.value, newTimeSelectOption];

      // set the value of the time (selected item)
      time.value = newTimeSelectOption.Id;
    }
  }
}

function getShiftSelectOptions() {
  const currentShift = props.schedule.Shift.Uname;

  if (shifts.value) {
    const selectedOption = shifts.value.find((x: { Label: string }) => x.Label === currentShift);

    if (selectedOption) {
      shiftOptions.value = [...shifts.value];
      shift.value = selectedOption.Id;
    } else {

      const newShiftSelectOption = {
        Id: "0",
        Label: selectedOption,
      };

      shiftOptions.value = [...shifts.value, newShiftSelectOption];

      shift.value = newShiftSelectOption.Id;
    }
  }
}

async function updatePerson(oldValue: number, newValue: unknown) {
  const updatePersonRequest: UpdatePersonRequest = {
    currentPersonId: oldValue,
    newPersonId: newValue,
  };
  const newPersonsList = await updatePersonAsync({
    Id: props.schedule.Id,
    UpdatePersonRequest: updatePersonRequest,
  });

  //broadcast to signalr
  await broadcast({
    messageType: "personUpdated",
    payload: {
      Id: props.schedule.Id,
      PersonList: newPersonsList,
    },
  });
}

async function updateStatusSelect(value: string | null) {
  if (value !== undefined && value !== null) {
    const request = {
      Id: props.schedule.Id,
      ScheduleState: value,
    };

    await updateScheduleState(request);

    //broadcast to signalr
    await broadcast({ messageType: "statusUpdated", payload: request });
  }
}

async function updateShiftSelect(value: string) {
  if (value !== undefined && value !== null) {
    const request = {
      Id: props.schedule.Id,
      Shift: value,
    };

    //update time
    await updateShift(request);

    //broadcast to signalr
    await broadcast({ messageType: "shiftUpdated", payload: request });
  }
}

async function updateTimeSelect(value: number) {
  if (value !== undefined && value !== null) {
    const request = {
      Id: props.schedule.Id,
      TimeId: value,
    };

    //update time
    await updateTime(request);

    //broadcast to signalr
    await broadcast({ messageType: "timeUpdated", payload: request });
  }
}

async function updateVehicle(value: string | null) {
  const vehicleRequest = {
    Id: props.schedule.Id,
    Vehicle: value,
  };

  await updateVehicleAsync(vehicleRequest);

  const newScheduleState = (value !== null && value !== undefined && value !== "") ? "1" : scheduleState.value;
  if (value !== null && value !== undefined && value !== "") {
    scheduleState.value = "1";
  }

  await broadcast({ messageType: "vehicleUpdated", payload: { ...vehicleRequest, ScheduleState: newScheduleState } });
}

const updateRemarkText = (newRemarkText: string) => {
  remarkText.value = newRemarkText;
};

watch(newPerson, async (value) => {
  if (value !== undefined) {
    const newPersonList = await addPersonAsync({
      Id: props.schedule.Id,
      PersonId: value,
    });

    //broadcast to signalr
    await broadcast({
      messageType: "personUpdated",
      payload: {
        Id: props.schedule.Id,
        PersonList: newPersonList,
      },
    });
  }
});

function sortHumanResources(
  humanResourceResourceGroups: HumanResourceResourceGroup[]
) {
  if (!humanResourceResourceGroups) return [];
  if (customer.customerName.value == "ijsselland") {
    return [...humanResourceResourceGroups].sort((a, b) => {
      if (
        a.ResourceGroup.Uname.includes("Ach") ||
        a.ResourceGroup.Uname.includes("Chauffeur")
      ) {
        return -1;
      } else {
        return 1;
      }
    });
  } else {
    return [...humanResourceResourceGroups].sort((a, b) =>
      a.ResourceGroup.Uname.localeCompare(b.ResourceGroup.Uname)
    );
  }
}

function parseRole(uname: string): "driver" | "nurse" {
  const name = uname.toLowerCase();

  if (name.includes("ach") || name.includes("chauffeur")) {
    return "driver";
  }

  return "nurse";
}

const colorVariantsVehicleNumberBackground = [
  {
    color: "yellow",
    code: "bg-cluster-yellow-primary text-neutral-1000 select-placeholder-black-override",
    softcode:
      "bg-cluster-yellow-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "red",
    code: "bg-cluster-red-primary text-neutral-0 select-placeholder-white-override",
    softcode:
      "bg-cluster-red-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "green",
    code: "bg-cluster-green-primary text-neutral-0 select-placeholder-white-override",
    softcode:
      "bg-cluster-green-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "brown",
    code: "bg-cluster-brown-primary text-neutral-0 select-placeholder-white-override",
    softcode:
      "bg-cluster-brown-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "blue",
    code: "bg-cluster-blue-primary text-neutral-0 select-placeholder-white-override",
    softcode:
      "bg-cluster-blue-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "black",
    code: "bg-cluster-black-primary text-neutral-0 select-placeholder-white-override",
    softcode:
      "bg-cluster-black-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "white",
    code: "bg-cluster-white-primary text-neutral-1000 select-placeholder-black-override",
    softcode:
      "bg-cluster-white-secondary text-neutral-1000 select-placeholder-black-override",
  },
  {
    color: "orange",
    code: "bg-cluster-orange-primary text-neutral-1000 select-placeholder-black-override",
    softcode:
      "bg-cluster-orange-secondary text-neutral-1000 select-placeholder-black-override",
  },
];

function vehicleNumberColor() {
  let returnValue = colorVariantsVehicleNumberBackground.find(
    (x) => x.color == props.schedule.Shift.ShiftGroup.Color
  )?.code;

  if (vehicleNumber.value == null || vehicleNumber.value == undefined) {
    returnValue += " bg-opacity-50";
  }

  return returnValue || "";
}

function personColor() {
  if (vehicleNumber.value == null || vehicleNumber.value == undefined) {
    return "bg-neutral-0";
  }

  return colorVariantsVehicleNumberBackground.find(
    (x) => x.color == props.schedule.Shift.ShiftGroup.Color
  )?.softcode;
}

function vehicleNumberBackground(inactive: boolean) {
  if (vehicleNumber.value == null || vehicleNumber.value == undefined) {
    return "repeating-linear-gradient";
  }

  let returnValue = colorVariantsVehicleNumberBackground.find(
    (x) => x.color == props.schedule.Shift.ShiftGroup.Color
  )?.code;

  if (inactive) {
    returnValue += " repeating-linear-gradient-inactive";
  }
  return returnValue;
}

function scheduleStateColor() {
  if (scheduleState.value == "1") {
    return "bg-[rgba(0,174,70,0.30)]";
  } else {
    return "bg-[rgba(217,39,0,0.30)]";
  }
}

function isInactive() {
  if (
    vehicleNumber.value != null &&
    vehicleNumber.value != undefined &&
    scheduleState.value != "1"
  ) {
    return true;
  } else {
    return false;
  }
}

function isZwolleWestenholte() {
  //check if shiftuname equals any of these values: A1Z2, D1Z2 or N1Z2
  if (
    props.schedule.Shift.Uname === "A1Z2" ||
    props.schedule.Shift.Uname === "D1Z2" ||
    props.schedule.Shift.Uname === "N1Z2"
  ) {
    return true;
  } else {
    return false;
  }
}

signalr.on("statusUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      scheduleState.value = message.ScheduleState;

      //if the schedulestate is 3 then this component should be removed from the screen using hidden
      displayed = message.ScheduleState !== "3";
    }
  });
});

signalr.on("personUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      humanResourceResourceGroups.value = message.PersonList;
      //reset newPerson
      newPerson.value = undefined;
    }
  });
});

signalr.on("vehicleUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      vehicleNumber.value = message.Vehicle;
      if (message.ScheduleState !== undefined) {
        scheduleState.value = message.ScheduleState;
      }
    }
  });
});

signalr.on("shiftUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      shift.value = message.Shift;
    }
  });
});

signalr.on("timeUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      time.value = message.TimeId;
    }
  });
});

signalr.on("remarkAddedOrUpdated", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      remarkText.value = message.RemarkText;
      hasRemark.value = !!message.RemarkText;
      showTrashIcon.value = hasRemark.value;
    }
  });
});

signalr.on("remarkDeleted", (...messages) => {
  messages.forEach((message) => {
    if (message.Id !== null && message.Id === props.schedule.Id) {
      remarkText.value = "";
      hasRemark.value = false;
      showTrashIcon.value = false;
    }
  });
});

watch(dataTime, getTimeSelectOptionFromDataTime);
onMounted(getTimeSelectOptionFromDataTime);

watch(shifts, getShiftSelectOptions);
onMounted(getShiftSelectOptions);

</script>

<style scoped>
.repeating-linear-gradient {
  background: repeating-linear-gradient(
    135deg,
    #ffffff,
    #ffffff 10px,
    #fffbfb 10px,
    #fffbfb 20px
  );
}
.repeating-linear-gradient-inactive {
  background: repeating-linear-gradient(
    135deg,
    #7f2925,
    #7f2925 10px,
    #7f2925b8 10px,
    #7f2925b8 20px
  );
}
</style>
